import { useEffect, useState } from 'react'
import { AlertTriangle, EyeOff, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { IconoItem } from '@/lib/cd2bIconos'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import Dato from './Dato'
import EditorAddenda from './EditorAddenda'
import { LineaIva } from './BloquesCobro'
import { dinero, sinEspacioNube } from './formato'

const MODULOS_PREVIEW = 6

/**
 * Preview corto (6 módulos) + botón "Ver todos" con modal para el resto — una edición
 * empresarial trae 15-20 módulos y mostrarlos todos en línea desbordaba en pantallas angostas
 * (reportado por el usuario). El modal es más liviano que agregar un tab nuevo solo para esto.
 */
function ModulosVendidos({ modulos }) {
  const [abierto, setAbierto] = useState(false)
  if (!modulos.length) {
    return (
      <>
        <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Módulos vendidos</p>
        <p className="text-body-sm text-neutral-800 dark:text-neutral-200 mt-0.5">—</p>
      </>
    )
  }
  const visibles = modulos.slice(0, MODULOS_PREVIEW)
  const restantes = modulos.length - visibles.length
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Módulos vendidos ({modulos.length})</p>
        {restantes > 0 && (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            className="inline-flex min-h-10 items-center text-caption font-medium text-primary-700 dark:text-primary-300 hover:underline shrink-0"
          >
            Ver todos
          </button>
        )}
      </div>
      <span className="flex flex-wrap gap-1.5 mt-1.5">
        {visibles.map((m) => (
          <Badge key={m.code} variant="secondary" className="inline-flex items-center gap-1">
            <IconoItem item={{ icono: m.icono, tipo: 'modulo' }} size={12} />
            {m.label}
          </Badge>
        ))}
        {restantes > 0 && (
          <button type="button" onClick={() => setAbierto(true)}>
            <Badge variant="outline" className="cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800">+{restantes} más</Badge>
          </button>
        )}
      </span>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Módulos vendidos</DialogTitle>
            <DialogDescription>{modulos.length} módulos incluidos en el paquete vigente.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 max-h-[60vh] overflow-y-auto">
            {modulos.map((m) => (
              <Badge key={m.code} variant="secondary" className="inline-flex items-center gap-1">
                <IconoItem item={{ icono: m.icono, tipo: 'modulo' }} size={12} />
                {m.label}
              </Badge>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ───────────────────── Paquete y alcance + Ocultar ───────────────────── */

export default function PaqueteYAlcance({ ficha, onCambio }) {
  const [ocultas, setOcultas] = useState(ficha.areasOcultas || [])
  const [guardando, setGuardando] = useState(false)
  const [editorAbierto, setEditorAbierto] = useState(false)
  // Áreas del paquete que el asesor está COMPONIENDO arriba (null si no hay editor abierto o aún
  // no hay impacto). No es el contrato: es la propuesta.
  const [areasPendientes, setAreasPendientes] = useState(null)
  const meta = ficha.catalogo?.areaMeta || {}

  useEffect(() => { setOcultas(ficha.areasOcultas || []) }, [ficha.areasOcultas])
  // Cerrar el editor descarta la propuesta: los toggles vuelven a hablar solo del contrato vigente.
  useEffect(() => { if (!editorAbierto) setAreasPendientes(null) }, [editorAbierto])

  // Áreas que el dueño puede ocultar: las que su contrato habilita (no las ya recortadas ni el núcleo).
  const ocultables = (ficha.areasContrato || []).filter((a) => a !== 'nucleo')
  // Un área que el paquete en curso NO entrega no se puede ocultar: ocultarla no significa nada
  // —el paquete ya se la quita— y dejar el interruptor vivo hacía creer que el editor de arriba y
  // esta lista hablan de lo mismo. Se deshabilita y se dice por qué.
  const pendientes = areasPendientes ? new Set(areasPendientes) : null
  const saleDelPaquete = (area) => !!pendientes && !pendientes.has(area)
  const nSalen = pendientes ? ocultables.filter(saleDelPaquete).length : 0

  const toggle = async (area, ocultar) => {
    const nueva = ocultar ? [...new Set([...ocultas, area])] : ocultas.filter((a) => a !== area)
    setOcultas(nueva)
    setGuardando(true)
    try {
      await carteraService.ocultarAreas(ficha.subscriptionId, nueva)
      toast.success(ocultar ? 'Área ocultada' : 'Área mostrada')
    } catch {
      toast.error('No se pudo actualizar')
      setOcultas(ocultas) // revertir
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Paquete actual */}
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Paquete vigente</h3>
          <Button variant="secondary" onClick={() => setEditorAbierto((v) => !v)}>
            {editorAbierto ? 'Cerrar' : 'Cambiar paquete'}
          </Button>
        </div>
        <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
          <Dato label="Edición">{ficha.edicion || (ficha.esDemo ? 'Sin paquete (demo)' : '—')}</Dato>
          <Dato label="Precio (MRR)">
            {ficha.mrr ? dinero(ficha.mrr, ficha.moneda)
              : ficha.precioPlan ? <>{dinero(ficha.precioPlan, ficha.moneda)} <span className="text-caption text-neutral-400 dark:text-neutral-500">(estimado)</span></>
              : '—'}
            <LineaIva c={ficha.cobroMensual} moneda={ficha.moneda} sufijo="/mes" />
          </Dato>
        </div>
        {/* Fila propia a todo el ancho: con 15-20 módulos vendidos (edición empresarial), meterlo
            en una celda de 1/3 del grid de arriba lo dejaba ilegible en pantallas angostas. */}
        <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800">
          <ModulosVendidos modulos={ficha.modulos || []} />
        </div>
        {/* Reventa cd-2b (docs/120 F8): SOLO el asesor ve esto — el cliente sigue viendo únicamente
            el precio final en su propio portal/documento (F-02). Son TOTALES congelados de la
            venta vigente; el desglose línea por línea vive en el panel de "Cambiar paquete" de
            abajo (impacto.despues.precioCongelado.lineas), que sí lo trae completo. */}
        {ficha.reventa?.modo === 'reventa' && (
          <div className="grid grid-cols-1 xs:grid-cols-3 gap-3 xs:gap-4 mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Dato label="Tu costo">{dinero(ficha.reventa.precioBaseMensual, ficha.moneda)}</Dato>
            <Dato label="Tu margen"><span className="text-accent-700 dark:text-accent-300 font-medium">+{dinero(ficha.reventa.margenMensual, ficha.moneda)}</span></Dato>
            <Dato label="Precio cliente">{dinero(ficha.reventa.precioBaseMensual + ficha.reventa.margenMensual, ficha.moneda)}</Dato>
          </div>
        )}
        {(ficha.areasOffContrato || []).length > 0 && (
          <p className="text-caption text-danger-700 dark:text-danger-300 mt-3 flex flex-wrap items-center gap-1.5">
            <AlertTriangle size={13} className="shrink-0" /> Recorte de contrato (no vendido, afecta al precio):{' '}
            {ficha.areasOffContrato.map((a) => meta[a]?.label || a).join(', ')}
          </p>
        )}
      </Card>

      {/* Editor de addenda */}
      {editorAbierto && (
        <EditorAddenda
          ficha={ficha}
          onAplicada={() => { setEditorAbierto(false); onCambio() }}
          onPaquetePendiente={setAreasPendientes}
        />
      )}

      {/* Ocultación del dueño */}
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-1">
          <EyeOff size={16} className="text-neutral-500 dark:text-neutral-400" />
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Ocultar áreas (preferencia del dueño)</h3>
        </div>
        {/* La lista sale de `areasContrato` —lo que la edición VIGENTE habilita—, no de lo que esté
            marcado en el editor de arriba. Decirlo evita leerla como incoherente cuando el asesor
            está componiendo un Micro y sigue viendo las áreas del PyME que el cliente aún tiene. */}
        <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-4">
          Solo aparecen las {ocultables.length} áreas que hoy entrega el paquete vigente
          {ficha.edicion ? <> (<span className="font-medium">{ficha.edicion}</span>)</> : null}. Si cambias
          la edición, esta lista se ajusta cuando la addenda se aplique. Ocultar un área no cambia el
          plan ni el precio: puedes volver a mostrarla cuando quieras.
          {!ficha.ocultacionDisponible && (
            <span className="text-warning-700 dark:text-warning-300">
              {sinEspacioNube(ficha)
                ? ' · Se podrá ocultar áreas cuando la empresa esté entregada a producción (todavía no tiene su espacio en la nube).'
                : ' · No se pudo leer el tenant ahora; intenta de nuevo en un momento.'}
            </span>
          )}
        </p>
        {nSalen > 0 && (
          <p className="text-caption text-warning-700 dark:text-warning-300 mb-3 flex items-start gap-1.5">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            {nSalen === 1 ? 'Un área queda' : `${nSalen} áreas quedan`} en gris: el paquete que estás
            componiendo arriba no {nSalen === 1 ? 'la entrega' : 'las entrega'}. Si guardas y aplicas la
            addenda, {nSalen === 1 ? 'desaparecerá' : 'desaparecerán'} de esta lista.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {ocultables.map((area) => {
            const oculta = ocultas.includes(area)
            const sale = saleDelPaquete(area)
            return (
              <label
                key={area}
                className={`flex min-h-11 items-center justify-between gap-3 py-1.5 border-b border-neutral-100 dark:border-neutral-800 ${sale ? 'opacity-50' : ''}`}
              >
                <span className="min-w-0 text-body-sm text-neutral-800 dark:text-neutral-200 flex flex-wrap items-center gap-1.5">
                  {oculta ? <EyeOff size={13} className="text-neutral-400 dark:text-neutral-500" /> : <Eye size={13} className="text-neutral-400 dark:text-neutral-500" />}
                  {meta[area]?.label || area}
                  {sale && <span className="text-micro text-warning-700 dark:text-warning-300">· la quita el paquete nuevo</span>}
                </span>
                <Switch
                  checked={!oculta}
                  disabled={guardando || !ficha.ocultacionDisponible || sale}
                  onCheckedChange={(activa) => toggle(area, !activa)}
                />
              </label>
            )
          })}
          {ocultables.length === 0 && <p className="text-caption text-neutral-500 dark:text-neutral-400">Sin áreas ocultables.</p>}
        </div>
      </Card>
    </div>
  )
}
